import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { GrowthChurnScore, GrowthClvScore, GrowthIntelligenceService, parseJsonRows } from './growth-intelligence.service';

interface ExplainabilityChip {
  code?: string;
  value?: string | number | boolean | null;
  note?: string;
  mode?: string;
  available?: boolean;
}

@Component({
  standalone: true,
  selector: 'ain-growth-scores',
  imports: [
    AinPageComponent, AinStateComponent, AinFormFieldComponent, InfoHintComponent, PartySelectorComponent,
    FormsModule, TableModule, ButtonModule, ChipModule,
    AinDatePipe
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.intelligence.scores.title')" [description]="t('growth.intelligence.scores.description')">
      <ain-info-hint page-title-addon [titleKey]="'growth.intelligence.scores.help.title'" [descriptionKey]="'growth.intelligence.scores.help.description'"
        [bulletKeys]="['growth.intelligence.scores.help.bullets.advisory', 'growth.intelligence.scores.help.bullets.explainability']" />

      <section class="card">
        <div class="grid">
          <ain-form-field [label]="t('growth.intelligence.scores.fields.party')">
            <ain-party-selector [(ngModel)]="partyId" />
          </ain-form-field>
        </div>
        <div class="toolbar">
          <button pButton type="button" [disabled]="!partyId" [label]="t('growth.intelligence.scores.actions.computeClv')" (click)="computeClv()"></button>
          <button pButton type="button" severity="secondary" [disabled]="!partyId" [label]="t('growth.intelligence.scores.actions.computeChurn')" (click)="computeChurn()"></button>
          <button pButton type="button" severity="secondary" [disabled]="!partyId" [label]="t('common.refresh')" (click)="loadLatest()"></button>
        </div>
      </section>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="card">
          <div class="section-title"><h3>{{ t('growth.intelligence.scores.sections.clv') }}</h3></div>
          @if (!clv()) {
            <ain-state type="empty" [title]="t('growth.intelligence.scores.emptyClv')" />
          } @else {
            <p-table [value]="[clv()!]">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.intelligence.scores.columns.score') }}</th>
                  <th>{{ t('growth.intelligence.scores.columns.estimatedValue') }}</th>
                  <th>{{ t('growth.intelligence.scores.columns.currency') }}</th>
                  <th>{{ t('growth.intelligence.scores.columns.computedAt') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.score }}</td>
                  <td>{{ row.estimatedValue }}</td>
                  <td>{{ row.currencyCode }}</td>
                  <td>{{ row.computedAtUtc | ainDate:'datetime' }}</td>
                </tr>
              </ng-template>
            </p-table>
            <div class="chips">
              @for (chip of clvExplainability(); track chip.code) {
                <p-chip [label]="formatChip(chip)" />
              }
            </div>
          }
        </section>

        <section class="card">
          <div class="section-title"><h3>{{ t('growth.intelligence.scores.sections.churn') }}</h3></div>
          @if (!churn()) {
            <ain-state type="empty" [title]="t('growth.intelligence.scores.emptyChurn')" />
          } @else {
            <p-table [value]="[churn()!]">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.intelligence.scores.columns.score') }}</th>
                  <th>{{ t('growth.intelligence.scores.columns.riskBand') }}</th>
                  <th>{{ t('growth.intelligence.scores.columns.computedAt') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.score }}</td>
                  <td>{{ row.riskBand }}</td>
                  <td>{{ row.computedAtUtc | ainDate:'datetime' }}</td>
                </tr>
              </ng-template>
            </p-table>
            <div class="chips">
              @for (chip of churnExplainability(); track chip.code) {
                <p-chip [label]="formatChip(chip)" />
              }
            </div>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); }
    .chips { display: flex; gap: 8px; flex-wrap: wrap; }
  `]
})
export class GrowthScoresComponent {
  private readonly service = inject(GrowthIntelligenceService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(false);
  readonly clv = signal<GrowthClvScore | null>(null);
  readonly churn = signal<GrowthChurnScore | null>(null);
  readonly clvExplainability = signal<ExplainabilityChip[]>([]);
  readonly churnExplainability = signal<ExplainabilityChip[]>([]);

  partyId: string | null = null;

  computeClv(): void {
    if (!this.partyId) return;
    this.loading.set(true);
    this.service.computeClv(this.partyId).subscribe({
      next: score => { this.clv.set(score); this.clvExplainability.set(parseJsonRows<ExplainabilityChip>(score.explainabilityJson)); this.loading.set(false); },
      error: () => this.loading.set(false)
    });
  }

  computeChurn(): void {
    if (!this.partyId) return;
    this.loading.set(true);
    this.service.computeChurn(this.partyId).subscribe({
      next: score => { this.churn.set(score); this.churnExplainability.set(parseJsonRows<ExplainabilityChip>(score.explainabilityJson)); this.loading.set(false); },
      error: () => this.loading.set(false)
    });
  }

  loadLatest(): void {
    if (!this.partyId) return;
    this.loading.set(true);
    this.service.getClv(this.partyId).subscribe({
      next: score => {
        if (score) {
          this.clv.set(score);
          this.clvExplainability.set(parseJsonRows<ExplainabilityChip>(score.explainabilityJson));
        }
      }
    });
    this.service.getChurn(this.partyId).subscribe({
      next: score => {
        if (score) {
          this.churn.set(score);
          this.churnExplainability.set(parseJsonRows<ExplainabilityChip>(score.explainabilityJson));
        }
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  formatChip(chip: ExplainabilityChip): string {
    const value = chip.value ?? chip.note ?? chip.mode ?? (chip.available === false ? 'n/a' : '-');
    return `${chip.code ?? 'factor'}: ${value}`;
  }

  t(key: string): string { return this.i18n.translate(key); }
}
